import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supportSettingsService } from '../services/supportSettingsService';
import {
  DEFAULT_HELPDESK_HOME_SCREEN,
  HelpdeskHomeScreen,
  resolveHelpdeskHomePath,
} from '../utils/helpdeskHomeScreen';
import {
  cacheAdminHelpdeskHomeScreen,
  readCachedAdminHelpdeskHomeScreen,
  readPersonalHelpdeskHomeScreen,
  writePersonalHelpdeskHomeScreen,
} from '../utils/helpdeskHomePreferenceStorage';

let adminDefaultLoadPromise: Promise<HelpdeskHomeScreen> | null = null;

const loadAdminDefaultHomeScreen = async (): Promise<HelpdeskHomeScreen> => {
  if (!adminDefaultLoadPromise)
  {
    adminDefaultLoadPromise = supportSettingsService
      .get()
      .then((settings) => cacheAdminHelpdeskHomeScreen(settings.defaultHomeScreen))
      .catch(() => readCachedAdminHelpdeskHomeScreen() ?? DEFAULT_HELPDESK_HOME_SCREEN)
      .finally(() => {
        adminDefaultLoadPromise = null;
      });
  }

  return adminDefaultLoadPromise;
};

export const useHelpdeskHomePath = () => {
  const { user } = useAuth();
  const [personalScreen, setPersonalScreen] = useState<HelpdeskHomeScreen | null>(() =>
    readPersonalHelpdeskHomeScreen(user?.id),
  );
  const [adminScreen, setAdminScreen] = useState<HelpdeskHomeScreen>(() =>
    readCachedAdminHelpdeskHomeScreen() ?? DEFAULT_HELPDESK_HOME_SCREEN,
  );

  useEffect(() => {
    setPersonalScreen(readPersonalHelpdeskHomeScreen(user?.id));
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;

    const loadAdminDefault = async () => {
      const screen = await loadAdminDefaultHomeScreen();
      if (cancelled)
      {
        return;
      }

      setAdminScreen(screen);
    };

    void loadAdminDefault();

    return () => {
      cancelled = true;
    };
  }, []);

  const effectiveScreen = personalScreen ?? adminScreen;
  const homePath = useMemo(() => resolveHelpdeskHomePath(effectiveScreen), [effectiveScreen]);

  const setPersonalPreference = useCallback((screen: HelpdeskHomeScreen | null) => {
    if (!user?.id)
    {
      return;
    }

    writePersonalHelpdeskHomeScreen(user.id, screen);
    setPersonalScreen(screen);
  }, [user?.id]);

  const syncAdminDefault = useCallback((screen: HelpdeskHomeScreen) => {
    setAdminScreen(cacheAdminHelpdeskHomeScreen(screen));
  }, []);

  return {
    homePath,
    effectiveScreen,
    personalScreen,
    adminScreen,
    setPersonalPreference,
    syncAdminDefault,
  };
};
